import React, { ReactElement, useState } from 'react';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import { useForm } from 'react-hook-form';
import { initialTwoFactorAuthFormValues, ITwoFactorAuthForm } from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import { schema } from 'pages/TwoFactorAuthPage/schemaValidation';
import { EButtonVariants } from 'constants/Buttons';
import { useAuth } from 'models/authContext';
import Button from 'components/atoms/Button';
import { AuthTemplate } from 'components/templates/AuthTemplate';
import { v4 as uuidv4 } from 'uuid';
import { FC_LICENSING_DEVICE_ID } from 'pages/LoginPage';
import { useTrustedDevice } from 'models/trustedDeviceContext';
import { ERouteLinks } from 'models/route';
import { useNavigate } from 'react-router-dom';
import { browserName, fullBrowserVersion } from 'react-device-detect';
import { AccountType } from 'constants/AccountType';
import { StyledPageSubTitle, StyledPageTitle } from 'theme/styles';
import { useTranslation } from 'react-i18next';
import { LoginType } from 'models/auth';

export interface ITwoFactorAuthPageProps {
  email: string;
  password: string;
  type: AccountType;
  loginType: LoginType;
  changePage: (p: number) => void;
}

const TwoFactorAuthPage = ({
  email,
  password,
  type,
  loginType,
  changePage,
}: ITwoFactorAuthPageProps): ReactElement => {
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: initialTwoFactorAuthFormValues,
    resolver: yupResolver(schema),
  });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { verifyAuthCode, logIn } = useAuth();
  const { addTrustedDevice } = useTrustedDevice();
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isResendAuthCodeLoading, setIsResendAuthCodeLoading] = useState(false);

  const onSubmit = async (data: ITwoFactorAuthForm) => {
    setIsLoginLoading(true);
    await verifyAuthCode(
      { username: email, password, type, code: data.authCode },
      (isFirstLogin) => {
        if (data.isRememberDevice) {
          const deviceToken = uuidv4();
          addTrustedDevice(
            {
              deviceToken,
              title: `${browserName} ${fullBrowserVersion} ${new Date().toLocaleString()}`,
              trustedDeviceTimeOptionUniqueId: data.trustedDeviceTimeOptionUniqueId,
            },
            () => {
              localStorage.setItem(FC_LICENSING_DEVICE_ID, deviceToken);
            },
          );
        }
        if (isFirstLogin) {
          changePage(6);
        } else {
          navigate(ERouteLinks.Dashboard);
          changePage(5);
        }
      },
      (message) => {
        setError('authCode', { type: 'custom', message });
      },
    );
    setIsLoginLoading(false);
  };

  return (
    <AuthTemplate>
      <Grid item container xs={12} gap={7}>
        <form onSubmit={handleSubmit(onSubmit as any)}>
          <Grid item container xs={12} gap={1}>
            <Grid item xs={12}>
              <StyledPageTitle variant="h4">{t('page.signin.sms.title')}</StyledPageTitle>
            </Grid>
            <Grid item xs={12}>
              <StyledPageSubTitle variant="body2">
                {loginType === LoginType.Sms
                  ? t('page.signin.sms.subtitle')
                  : t('page.signin.email.subtitle')}
              </StyledPageSubTitle>
            </Grid>
          </Grid>

          <Grid item container xs={12} gap={3}>
            <Grid item xs={12} gap={2} mt={2}>
              <Grid item xs={12}>
                <FormInput
                  name="authCode"
                  label={
                    loginType === LoginType.Sms
                      ? t('page.signin.input.authCode.sms')
                      : t('page.signin.input.authCode.email')
                  }
                  control={control}
                  errors={errors}
                  autoFocus={true}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: { xs: 32, sm: 40 },
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Grid item container xs={12} gap={1.3} justifyItems="center">
              <Grid item xs={12}>
                <Button
                  label={t('page.signin.button.verifyCode')}
                  type="submit"
                  isLoading={isLoginLoading}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  label={t('page.signin.button.resendCode')}
                  variant={EButtonVariants.outlined}
                  isLoading={isResendAuthCodeLoading}
                  onClick={async () => {
                    setIsResendAuthCodeLoading(true);
                    logIn({
                      type,
                      username: email,
                      password,
                      loginType,
                    });
                    setIsResendAuthCodeLoading(false);
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  id="start-page"
                  label={t('page.signin.button.backToStart')}
                  variant={EButtonVariants.text}
                  isLoading={isLoginLoading}
                  fullWidth
                  onClick={() => changePage(1)}
                />
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </AuthTemplate>
  );
};
export { TwoFactorAuthPage };
